import { cn } from '@/shared/utils/cn';
import {
  AvatarWithStackProps,
  AvatarSize,
  AvatarColor,
  AvatarTheme,
  AvatarStackItem,
  AvatarProps,
} from '../types';
import { Avatar } from '../index';
import {
  STACK_NEGATIVE_MARGINS,
  STACK_PADDING_RIGHT,
  DEFAULT_SIZE,
  DEFAULT_COLOR,
  DEFAULT_THEME,
} from '../constants';

/**
 * ============================================
 * AVATAR STACK - ATOMIC COMPONENT
 * ============================================
 * Componente que muestra múltiples avatares apilados con márgenes negativos
 *
 * @component
 * @example
 * ```tsx
 * <AvatarStack
 *   stack={[
 *     { src: '...' }, // type='people' se infiere automáticamente
 *     { src: '...' }, // type='people' se infiere automáticamente
 *     { type: 'letter', text: 'A' }
 *   ]}
 *   size="md"
 * />
 *
 * @example
 * // Con límite máximo de avatares visibles
 * <AvatarStack
 *   stack={[
 *     { src: '...' },
 *     { src: '...' },
 *     // ... 8 avatares más
 *   ]}
 *   size="md"
 *   maxVisible={5} // Muestra 5 avatares + contador "+5"
 * />
 * ```
 */
export const AvatarStack = ({
  stack,
  size = DEFAULT_SIZE,
  className,
  maxVisible,
  counterColor = DEFAULT_COLOR,
  counterTheme = DEFAULT_THEME,
  stroke = false,
}: AvatarWithStackProps) => {
  if (!stack || stack.length === 0) {
    return null;
  }

  const paddingRight = STACK_PADDING_RIGHT[size];

  // Calcular qué avatares mostrar y si hay que agregar contador
  let avataresToShow = stack;
  let remainingCount = 0;

  if (maxVisible !== undefined && maxVisible > 0 && stack.length > maxVisible) {
    avataresToShow = stack.slice(0, maxVisible);
    remainingCount = stack.length - maxVisible;
  }

  // Mapeo de tamaños a valores de píxeles para márgenes negativos
  const negativeMarginPixels: Record<AvatarSize, number> = {
    xxs: -8,
    xs: -12,
    sm: -16,
    md: -20,
    lg: -24,
    xl: -32,
  };

  // Mapeo de tamaños a valores de píxeles para padding derecho
  const paddingRightPixels: Record<AvatarSize, number> = {
    xxs: 8,
    xs: 12,
    sm: 16,
    md: 20,
    lg: 24,
    xl: 32,
  };

  return (
    <div
      className={cn('ui-imj', 'ui-flex ui-items-center', className)}
      style={{ paddingRight: `${paddingRightPixels[size]}px` }}
    >
      {avataresToShow.map((avatarProps, index) => {
        // Aplicar el tamaño del stack a cada avatar si no tiene tamaño definido
        const avatarSize = avatarProps.size || size;

        // Si tiene src, sobrescribir automáticamente el type a 'people'
        // Esto permite pasar solo { src: '...' } sin especificar type
        let processedProps: AvatarStackItem = avatarProps;
        if ('src' in avatarProps && avatarProps.src) {
          processedProps = {
            ...avatarProps,
            type: 'people' as const,
          };
        }

        // Aplicar stroke si está definido a nivel de stack
        if (stroke) {
          processedProps = {
            ...processedProps,
            stroke: true,
          };
        }

        return (
          <div
            key={index}
            className="ui-shrink-0"
            style={
              index < avataresToShow.length - 1 || remainingCount > 0
                ? { marginRight: `${negativeMarginPixels[size]}px` }
                : undefined
            }
          >
            <Avatar {...(processedProps as AvatarProps)} size={avatarSize} />
          </div>
        );
      })}

      {/* Mostrar contador si hay avatares ocultos */}
      {remainingCount > 0 && (
        <div className="ui-shrink-0">
          <Avatar
            type="letter"
            text={`+${remainingCount}`}
            size={size}
            theme={counterTheme}
            color={counterColor}
            stroke={stroke}
          />
        </div>
      )}
    </div>
  );
};
